import { useCallback, useEffect, useState } from 'react';
import { API_URL, fetchApi } from '../services/api/client';
import {
  convertAmount,
  roundMoney,
  toCurrencyCode,
  toCurrencySymbol,
} from '../utils/currency';

type FxRatesResponse = {
  base: string;
  rates: Record<string, number>;
  date?: string | null;
};

const CACHE_KEY = 'mytraveldiary.fxRates';
const CACHE_TTL_MS = 6 * 60 * 60 * 1000;

function readCache(): FxRatesResponse | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ts: number; data: FxRatesResponse };
    if (!parsed?.data?.rates || Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache(data: FxRatesResponse): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    /* ignore */
  }
}

export function useFxRates(displayCurrency: string) {
  const [rates, setRates] = useState<Record<string, number> | null>(() => readCache()?.rates ?? null);
  const displayCode = toCurrencyCode(displayCurrency, 'RUB');
  const displaySymbol = toCurrencySymbol(displayCurrency);

  useEffect(() => {
    let cancelled = false;
    const cached = readCache();
    if (cached?.rates) setRates(cached.rates);
    void fetchApi<FxRatesResponse>(`${API_URL}/fx/rates`)
      .then(data => {
        if (cancelled || !data?.rates) return;
        writeCache(data);
        setRates(data.rates);
      })
      .catch(() => {
        /* keep cache / no conversion */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toDisplay = useCallback(
    (amount: number, fromCurrency: string | null | undefined = 'RUB') =>
      roundMoney(convertAmount(amount, fromCurrency, displayCode, rates)),
    [displayCode, rates],
  );

  return { rates, displayCode, displaySymbol, toDisplay };
}
